import { FileText, Image as ImageIcon, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ItemFormDialog = ({ open, onOpenChange, editingItem, formData, imageFile, onFormChange, onImageChange, onSubmit }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 rounded-sm">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold uppercase" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {editingItem ? "Edit Item" : "Add New Item"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="uppercase-label text-xs text-slate-600 font-bold">Name</Label>
            <Input data-testid="item-name-input" value={formData.name} onChange={(e) => onFormChange({ ...formData, name: e.target.value })} required className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
          </div>
          <div>
            <Label className="uppercase-label text-xs text-slate-600 font-bold">Part Number</Label>
            <Input data-testid="item-part-number-input" value={formData.part_number} onChange={(e) => onFormChange({ ...formData, part_number: e.target.value })} required className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="uppercase-label text-xs text-slate-600 font-bold">Quantity</Label>
              <Input data-testid="item-quantity-input" type="number" value={formData.quantity} onChange={(e) => onFormChange({ ...formData, quantity: parseInt(e.target.value) })} required min="0" className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
            </div>
            <div>
              <Label className="uppercase-label text-xs text-slate-600 font-bold">Reorder Level</Label>
              <Input data-testid="item-reorder-input" type="number" value={formData.reorder_level} onChange={(e) => onFormChange({ ...formData, reorder_level: parseInt(e.target.value) })} required min="0" className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="uppercase-label text-xs text-slate-600 font-bold">Category <span className="text-slate-400 font-normal">(Optional)</span></Label>
              <Input data-testid="item-category-input" value={formData.category} onChange={(e) => onFormChange({ ...formData, category: e.target.value })} placeholder="e.g., Hydraulic, Engine" className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
            </div>
            <div>
              <Label className="uppercase-label text-xs text-slate-600 font-bold">Machine Model <span className="text-slate-400 font-normal">(Optional)</span></Label>
              <Input data-testid="item-machine-model-input" value={formData.machine_model} onChange={(e) => onFormChange({ ...formData, machine_model: e.target.value })} placeholder="e.g., EX-200, D6T" className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="uppercase-label text-xs text-slate-600 font-bold">Location</Label>
              <Input data-testid="item-location-input" value={formData.location} onChange={(e) => onFormChange({ ...formData, location: e.target.value })} placeholder="e.g., Warehouse A - Shelf 12" className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
            </div>
            <div>
              <Label className="uppercase-label text-xs text-slate-600 font-bold">Brand</Label>
              <Input data-testid="item-brand-input" value={formData.brand} onChange={(e) => onFormChange({ ...formData, brand: e.target.value })} placeholder="e.g., Caterpillar" className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
            </div>
          </div>
          <div>
            <Label className="uppercase-label text-xs text-slate-600 font-bold">Weight (kg)</Label>
            <Input data-testid="item-weight-input" type="number" step="0.1" value={formData.weight} onChange={(e) => onFormChange({ ...formData, weight: parseFloat(e.target.value) || 0 })} className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
          </div>
          <div>
            <Label className="uppercase-label text-xs text-slate-600 font-bold">Item Image <span className="text-slate-400 font-normal">(Optional)</span></Label>
            {formData.image_url && !imageFile && (
              <div className="mt-1 mb-2 p-2 bg-slate-50 border border-slate-200 rounded-sm">
                <div className="flex items-center gap-3">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${formData.image_url}`} alt="Current" className="w-16 h-16 object-cover rounded-sm border border-slate-300" />
                  <div>
                    <p className="text-xs text-slate-500">Current image attached</p>
                    <button type="button" onClick={() => onFormChange({ ...formData, image_url: "" })} className="text-xs text-red-500 hover:text-red-700 mt-1">Remove image</button>
                  </div>
                </div>
              </div>
            )}
            <Input data-testid="item-image-input" type="file" accept="image/*,.pdf" onChange={(e) => onImageChange(e.target.files[0])} className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
            {imageFile && <p className="text-xs text-green-600 mt-1">New image selected: {imageFile.name}</p>}
          </div>
          <div>
            <Label className="uppercase-label text-xs text-slate-600 font-bold">Notes <span className="text-slate-400 font-normal">(Optional)</span></Label>
            <Input data-testid="item-notes-input" value={formData.notes} onChange={(e) => onFormChange({ ...formData, notes: e.target.value })} placeholder="Special handling, packaging requirements, etc." className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="submit" data-testid="submit-item-button" className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-sm btn-snap uppercase font-bold">{editingItem ? "Update" : "Add"}</Button>
            <Button type="button" data-testid="cancel-item-button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 border-2 border-slate-200 hover:border-slate-900 rounded-sm btn-snap uppercase font-bold">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const QuickEditDialog = ({ open, onOpenChange, item, quantity, transactionType, onQuantityChange, onTypeChange, onSubmit }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 rounded-sm">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold uppercase" style={{ fontFamily: 'Chivo, sans-serif' }}>Quick Update Quantity</DialogTitle>
        </DialogHeader>
        {item && (
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
              <p className="text-sm text-slate-600 font-bold uppercase tracking-wider" style={{ fontFamily: 'Chivo, sans-serif' }}>{item.name}</p>
              <p className="text-xs text-slate-500 mt-1">Part#: {item.part_number}</p>
              <p className="text-lg font-bold text-slate-900 mt-2">Current: {item.quantity} units</p>
            </div>
            <div>
              <Label className="uppercase-label text-xs text-slate-600 font-bold">New Quantity</Label>
              <Input data-testid="quick-quantity-input" type="number" value={quantity} onChange={(e) => onQuantityChange(parseInt(e.target.value))} min="0" className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50" />
            </div>
            <div>
              <Label className="uppercase-label text-xs text-slate-600 font-bold">Transaction Type</Label>
              <Select value={transactionType} onValueChange={onTypeChange}>
                <SelectTrigger data-testid="transaction-type-select" className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-sm">
                  <SelectItem value="sale">Sale</SelectItem>
                  <SelectItem value="purchase">Purchase</SelectItem>
                  <SelectItem value="adjustment">Adjustment</SelectItem>
                  <SelectItem value="return">Return</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={onSubmit} data-testid="submit-quick-update-button" className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-sm btn-snap uppercase font-bold">Update</Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 border-2 border-slate-200 hover:border-slate-900 rounded-sm btn-snap uppercase font-bold">Cancel</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const NotesDialog = ({ open, onOpenChange, item }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-white border-slate-200 rounded-sm">
      <DialogHeader><DialogTitle className="text-2xl font-bold uppercase" style={{ fontFamily: 'Chivo, sans-serif' }}>Item Notes</DialogTitle></DialogHeader>
      {item && (
        <div className="space-y-4">
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
            <p className="text-sm font-bold text-slate-900 mb-2">{item.name}</p>
            <p className="text-xs text-slate-500">Part#: {item.part_number}</p>
          </div>
          <div className="p-4 bg-white border border-slate-200 rounded-sm">
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{item.notes || 'No notes available'}</p>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const LedgerDialog = ({ open, onOpenChange, item, data }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-white border-slate-200 rounded-sm max-w-3xl">
      <DialogHeader><DialogTitle className="text-2xl font-bold uppercase" style={{ fontFamily: 'Chivo, sans-serif' }}>Item Ledger</DialogTitle></DialogHeader>
      {item && (
        <div className="space-y-4">
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
            <p className="text-sm font-bold text-slate-900 mb-2">{item.name}</p>
            <p className="text-xs text-slate-500">Part#: {item.part_number}</p>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="mx-auto text-slate-300 mb-3" size={48} />
                <p className="text-slate-500">No transaction history</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-bold text-slate-600 uppercase">Date</th>
                    <th className="text-left px-4 py-2 text-xs font-bold text-slate-600 uppercase">Type</th>
                    <th className="text-right px-4 py-2 text-xs font-bold text-slate-600 uppercase">Quantity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-700">{new Date(transaction.created_at).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 text-xs font-bold uppercase rounded-sm ${
                          transaction.transaction_type === 'sale' ? 'bg-red-100 text-red-700' :
                          transaction.transaction_type === 'purchase' ? 'bg-green-100 text-green-700' :
                          transaction.transaction_type === 'return' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>{transaction.transaction_type}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{transaction.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const ImageDialog = ({ open, onOpenChange, item }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-white border-slate-200 rounded-sm">
      <DialogHeader><DialogTitle className="text-2xl font-bold uppercase" style={{ fontFamily: 'Chivo, sans-serif' }}>Item Image</DialogTitle></DialogHeader>
      {item && (
        <div className="space-y-4">
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
            <p className="text-sm font-bold text-slate-900 mb-2">{item.name}</p>
            <p className="text-xs text-slate-500">Part#: {item.part_number}</p>
          </div>
          {item.image_url ? (
            <div className="flex items-center justify-center p-4 bg-slate-50 border border-slate-200 rounded-sm">
              <img src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`} alt={item.name} className="max-w-full max-h-96 object-contain" />
            </div>
          ) : (
            <div className="p-8 text-center">
              <ImageIcon className="mx-auto text-slate-300 mb-3" size={48} />
              <p className="text-slate-500">No image available</p>
            </div>
          )}
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const BulkAddDialog = ({ open, onOpenChange, items, onAddRow, onRemoveRow, onUpdateItem, onSubmit, onSetItems, rowErrors, submitting }) => {
  const COLUMNS = ['part_number', 'name', 'brand', 'location', 'weight', 'quantity', 'reorder_level'];

  const handlePaste = (e) => {
    const clipboardData = e.clipboardData.getData('text');
    if (!clipboardData) return;

    const lines = clipboardData.split('\n').filter(line => line.trim());
    if (lines.length <= 1 && !clipboardData.includes('\t')) return; // Not tabular data

    e.preventDefault();

    const newItems = lines.map(line => {
      const cols = line.split('\t');
      return {
        part_number: (cols[0] || '').trim(),
        name: (cols[1] || '').trim(),
        brand: (cols[2] || '').trim(),
        location: (cols[3] || '').trim(),
        weight: parseFloat(cols[4]) || 0,
        quantity: parseInt(cols[5]) || 0,
        reorder_level: parseInt(cols[6]) || 10,
        category: '', machine_model: '', notes: '',
      };
    }).filter(item => item.part_number || item.name);

    if (newItems.length > 0 && onSetItems) {
      // Replace empty rows or append
      const hasData = items.some(i => i.part_number || i.name);
      onSetItems(hasData ? [...items, ...newItems] : newItems);
    }
  };

  const errorCount = rowErrors ? Object.keys(rowErrors).length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 rounded-sm max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-2xl font-bold uppercase" style={{ fontFamily: 'Chivo, sans-serif' }}>Bulk Add Items</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
            <p className="text-sm text-blue-800">
              <strong>Paste from Excel/Sheets:</strong> Copy rows with columns in this order: <span className="font-mono text-xs">Part Number, Name, Brand, Location, Weight, Quantity, Reorder Level</span> and paste anywhere in the grid below. Rows will be added automatically.
            </p>
          </div>
          {errorCount > 0 && (
            <div className="p-3 bg-red-50 border border-red-300 rounded-sm" data-testid="bulk-add-error-banner">
              <p className="text-sm font-bold text-red-700">{errorCount} row{errorCount !== 1 ? 's' : ''} failed — fix the highlighted rows and try again</p>
            </div>
          )}
          <div className="overflow-x-auto" onPaste={handlePaste} data-testid="bulk-add-grid">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="text-center px-2 py-2 text-xs font-bold text-slate-600 uppercase w-8">#</th>
                  <th className="text-left px-2 py-2 text-xs font-bold text-slate-600 uppercase">Part Number*</th>
                  <th className="text-left px-2 py-2 text-xs font-bold text-slate-600 uppercase">Name*</th>
                  <th className="text-left px-2 py-2 text-xs font-bold text-slate-600 uppercase">Brand</th>
                  <th className="text-left px-2 py-2 text-xs font-bold text-slate-600 uppercase">Location</th>
                  <th className="text-right px-2 py-2 text-xs font-bold text-slate-600 uppercase">Weight (kg)</th>
                  <th className="text-right px-2 py-2 text-xs font-bold text-slate-600 uppercase">Quantity</th>
                  <th className="text-right px-2 py-2 text-xs font-bold text-slate-600 uppercase">Reorder Lvl</th>
                  <th className="text-center px-2 py-2 text-xs font-bold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const hasError = rowErrors && rowErrors[index];
                  return (
                    <tr key={index} className={`border-b ${hasError ? 'bg-red-50 border-red-300' : 'border-slate-200'}`} data-testid={`bulk-add-row-${index}`}>
                      <td className="px-2 py-2 text-center text-xs text-slate-400">{index + 1}</td>
                      <td className="px-2 py-2">
                        <Input value={item.part_number} onChange={(e) => onUpdateItem(index, 'part_number', e.target.value)} placeholder="EXC-HP-001" className={`rounded-sm text-sm ${hasError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`} />
                      </td>
                      <td className="px-2 py-2">
                        <Input value={item.name} onChange={(e) => onUpdateItem(index, 'name', e.target.value)} placeholder="Item Name" className={`rounded-sm text-sm ${hasError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`} />
                      </td>
                      <td className="px-2 py-2"><Input value={item.brand} onChange={(e) => onUpdateItem(index, 'brand', e.target.value)} placeholder="Brand" className="rounded-sm border-slate-300 text-sm" /></td>
                      <td className="px-2 py-2"><Input value={item.location} onChange={(e) => onUpdateItem(index, 'location', e.target.value)} placeholder="Warehouse A" className="rounded-sm border-slate-300 text-sm" /></td>
                      <td className="px-2 py-2"><Input type="number" step="0.1" value={item.weight} onChange={(e) => onUpdateItem(index, 'weight', parseFloat(e.target.value) || 0)} className="rounded-sm border-slate-300 text-sm text-right" /></td>
                      <td className="px-2 py-2"><Input type="number" value={item.quantity} onChange={(e) => onUpdateItem(index, 'quantity', parseInt(e.target.value) || 0)} className="rounded-sm border-slate-300 text-sm text-right" /></td>
                      <td className="px-2 py-2"><Input type="number" value={item.reorder_level} onChange={(e) => onUpdateItem(index, 'reorder_level', parseInt(e.target.value) || 10)} className="rounded-sm border-slate-300 text-sm text-right" /></td>
                      <td className="px-2 py-2 text-center"><Button size="sm" variant="ghost" onClick={() => onRemoveRow(index)} className="text-red-600 hover:bg-red-50 rounded-sm" disabled={items.length === 1}><X size={16} /></Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Inline error messages below rows */}
            {rowErrors && Object.keys(rowErrors).length > 0 && (
              <div className="mt-2 space-y-1 px-2" data-testid="bulk-add-row-errors">
                {Object.entries(rowErrors).map(([rowIdx, msgs]) => (
                  <p key={rowIdx} className="text-xs text-red-600 font-medium">Row {parseInt(rowIdx) + 1}: {Array.isArray(msgs) ? msgs.join(', ') : msgs}</p>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button onClick={onAddRow} variant="outline" className="border-2 border-slate-200 hover:border-slate-900 rounded-sm btn-snap uppercase font-bold"><Plus size={18} className="mr-2" /> Add Row</Button>
            <p className="text-xs text-slate-400 self-center">{items.length} row{items.length !== 1 ? 's' : ''}{errorCount > 0 ? ` (${errorCount} with errors)` : ''}</p>
            <div className="flex-1"></div>
            <Button onClick={onSubmit} disabled={submitting} data-testid="submit-bulk-items" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm btn-snap uppercase font-bold">{submitting ? "Adding..." : "Add All Items"}</Button>
            <Button onClick={() => onOpenChange(false)} variant="outline" className="border-2 border-slate-200 hover:border-slate-900 rounded-sm btn-snap uppercase font-bold">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { ItemFormDialog, QuickEditDialog, NotesDialog, LedgerDialog, ImageDialog, BulkAddDialog };
